/**
 * Hvad dækker den indloggede kundes egen plads på et hold: svaret fra
 * GET /api/widget/classes/price (`coverage`), som bruger samme valg som widgettens booking
 * (apps/holdstyring/src/lib/widget/widget-coverage.ts).
 *
 * Tog 21-testen 25-10-2026: booking-vinduet viste drop-in-pris og betalingsvalg til et medlem,
 * hvis klippekort dækkede holdet, og bookingen trak så et klip uden at sige det. Nu viser
 * vinduet «Dækket af dit klippekort · 1 klip» (som booking-vinduet på hjemmesiden) og skjuler
 * prisen, rabatkoden og betalingsvalget for kundens egen plads.
 *
 * Ren modul uden afhængigheder ud over members-only.
 */
import { paysDropIn, type MembersOnlyFlags } from './members-only'

export type WidgetCoverage =
  | { kind: 'subscription' }
  | { kind: 'klippekort'; clips: number; clipsLeftAfter?: number }
  | null

/** Pladsen er dækket (abonnement eller klip), så der er intet at betale for den. */
export function erDaekket(coverage: WidgetCoverage | undefined): boolean {
  return coverage != null
}

/**
 * Betaler kundens egen plads drop-in? Kun når holdet kan betales som drop-in, og hverken
 * abonnement eller klippekort dækker det. Et members-only drop-in-hold («omfattes ikke af
 * medlemskab») betales altid. `undefined` = dækningen er ikke hentet (ikke logget ind,
 * eller opslaget fejlede): så vises drop-in-prisen som før.
 */
export function egenPladsBetalerDropIn(
  cls: MembersOnlyFlags & { dropInPriceDkk?: number | null },
  coverage: WidgetCoverage | undefined,
): boolean {
  if (!paysDropIn(cls)) return false
  if (cls.excludeFromMembership === true) return true
  return !erDaekket(coverage)
}

/** Antal klip, klippekortet bruger på holdet, når et klip dækker pladsen; ellers null. */
export function klipPaaHoldet(coverage: WidgetCoverage | undefined): number | null {
  return coverage?.kind === 'klippekort' ? Math.max(1, coverage.clips) : null
}

/**
 * Kan kunden vælge drop-in i stedet for et klip («Brug klippekort», som booking-vinduet på hjemmesiden, A4)?
 * Kun når et klippekort dækker pladsen, holdet kan betales som drop-in, og holdet ikke kræver medlemskab
 * (dér kan klippet ikke fravælges). Serveren håndhæver samme regel (useKlippekort: false).
 */
export function kanFravaelgeKlip(
  cls: MembersOnlyFlags & { dropInPriceDkk?: number | null },
  coverage: WidgetCoverage | undefined,
): boolean {
  return coverage?.kind === 'klippekort' && paysDropIn(cls) && cls.membershipRequired !== true && cls.excludeFromMembership !== true
}

/** Dækningen efter kundens valg: fravalgt klip = ingen dækning (pladsen betales som drop-in). */
export function daekningEfterValg(
  cls: MembersOnlyFlags & { dropInPriceDkk?: number | null },
  coverage: WidgetCoverage | undefined,
  brugKlip: boolean,
): WidgetCoverage | undefined {
  return !brugKlip && kanFravaelgeKlip(cls, coverage) ? null : coverage
}
